"use client";

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Filter, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { useDispatch, useSelector } from "react-redux";
import { dateFilterData, intakeTableColumns } from "../../config/constant";
import {
  fetchDataForMultipleSelect,
  fetchFilterTotalApplications,
} from "../../api/userService";
import {
  setAppetiteData,
  setCurrentPage,
  setActiveFilters,
  setTotalApplications,
} from "../../store/ApplicationSlice";
import { FilterMultiSelect } from "./filter-multiselect";

export function ApplicationFilters({
  setIsLoading,
  columnFilters,
  dateFilters,
  setDateFilters,
  setColumnFilters,
  handleUpdateData,
}) {
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const uniqueValues = useSelector((state) => state.intakeData.uniqueValues);
  const activeFilters = useSelector((state) => state.intakeData.activeFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const removeFilter = (filter) => {
    dispatch(setActiveFilters(activeFilters.filter((f) => f !== filter)));
  };

  const clearAllFilters = async () => {
    dispatch(setActiveFilters([]));
    setColumnFilters({});
    setDateFilters(dateFilterData);
    await handleUpdateData(1, {}, dateFilterData);
    dispatch(setCurrentPage(1));
  };

  const sortedColumns = [
    ...intakeTableColumns.filter((col) => col.key !== "date"),
    intakeTableColumns.find((col) => col.key === "date"),
  ];

  // Handle filter change
  const handleFilterChange = (column, selectedValue) => {
    if (selectedValue) {
      setColumnFilters((prev) => ({
        ...prev,
        [column]: selectedValue,
      }));

      const columnLabel =
        intakeTableColumns.find((col) => col.key === column)?.label || column;

      const newFilter = `${columnLabel}: ${selectedValue}`;

      // Dispatch instead of local setActiveFilters
      const filtered = activeFilters.filter(
        (f) => !f.startsWith(`${columnLabel}:`)
      );
      dispatch(setActiveFilters([...filtered, newFilter]));
    }
  };

  //function to update dateFilters state
  const updateFilter = (key, value) => {
    setDateFilters((prev) => ({ ...prev, [key]: value }));

    const formattedValue = value ? value.toISOString().split("T")[0] : "";

    const labelMap = {
      startDate: "Start Date",
      endDate: "End Date",
    };

    const newFilter = `${labelMap[key]}: ${formattedValue}`;

    const filtered = activeFilters.filter(
      (f) => !f.startsWith(`${labelMap[key]}:`)
    );
    dispatch(
      setActiveFilters(formattedValue ? [...filtered, newFilter] : filtered)
    );
  };

  const handleApplyClick = async () => {
    setIsLoading(true);
    const response = await fetchDataForMultipleSelect(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken
    );

    const totalCount = await fetchFilterTotalApplications(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken
    );

    if (response) {
      dispatch(setAppetiteData(response)); // Update Redux store
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    dispatch(setCurrentPage(1));
    setIsLoading(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => setShowAdvancedFilters(true)}
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
        </Button>

        <Dialog
          open={showAdvancedFilters}
          onOpenChange={setShowAdvancedFilters}
        >
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle>Advanced Filters</DialogTitle>
              <DialogDescription>
                Customize your application view with advanced filtering options.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 py-4">
              {sortedColumns.map(({ key, label }) => {
                const options = uniqueValues?.[key] || [];

                // If key is 'date', render the date range UI
                if (key === "date") {
                  const renderDateInput = (id, label) => {
                    const dateValue = dateFilters[id];
                    const formattedDate = dateValue
                      ? dateValue.toISOString().split("T")[0]
                      : "";

                    return (
                      <Input
                        key={id}
                        type="date"
                        className="flex-1"
                        value={formattedDate}
                        onChange={(e) => {
                          const newValue = e.target.value
                            ? new Date(e.target.value)
                            : null;
                          updateFilter(id, newValue);
                        }}
                        aria-label={label}
                      />
                    );
                  };

                  return (
                    <div key={key} className="space-y-2">
                      <label className=" font-medium">Date Range</label>
                      <div className="flex gap-2">
                        {renderDateInput("startDate", "Start Date")}
                        <span className="flex items-center">to</span>
                        {renderDateInput("endDate", "End Date")}
                      </div>
                    </div>
                  );
                }

                // Skip if no options
                if (!options.length) return null;

                // Render Select for all other keys
                return (
                  <FilterMultiSelect
                    key={key}
                    keyName={key}
                    label={label}
                    value={columnFilters[key] || []}
                    onValueChange={(value) => handleFilterChange(key, value)}
                    options={options}
                    openDropdownKey={openDropdownKey}
                    setOpenDropdownKey={setOpenDropdownKey}
                  />
                );
              })}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAdvancedFilters(false);
                  setColumnFilters({});
                  setDateFilters(dateFilterData);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-[#FF5533] hover:bg-[#E04A2F] text-white"
                onClick={() => {
                  setShowAdvancedFilters(false);
                  handleApplyClick();
                }}
              >
                Apply Filters
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex items-center flex-wrap gap-2">
          <div
            className="flex items-center gap-2 overflow-x-auto whitespace-nowrap"
            style={{ width: "500px" }} // customize width here
          >
            {activeFilters.map((filter, idx) => (
              <Badge
                key={`${filter}-${idx}`}
                variant="outline"
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1 flex-shrink-0"
              >
                {filter}
                <X
                  role="button"
                  aria-label={`Remove filter ${filter}`}
                  className="h-3 w-3 ml-1 cursor-pointer"
                  onClick={() => removeFilter(filter)}
                />
              </Badge>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-sm h-7 text-gray-500 hover:text-gray-700 flex-shrink-0"
            onClick={clearAllFilters}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
